/** Safe display fields only; evidence and delivery payloads remain service-only. */
export interface InvoiceFeeDisplay {
  id: string;
  loanId: string;
  encompassLoanId: string;
  attachmentId: string;
  loanNumber: string;
  borrowerName: string | null;
  documentTitle: string;
  invoiceFolder: string | null;
  invoiceNumber: string | null;
  orderNumber: string | null;
  vendor: string | null;
  outcome: 'waiting' | 'passed' | 'overage' | 'review';
  reason: string | null;
  reasonCode?: string | null;
  identityNote?: string | null;
  appraisalReportId?: string | null;
  invoiceCents: number | null;
  invoiceAmountStatus?: 'verified' | 'extracted' | 'unavailable';
  feeLine: string | null;
  feeCents: number | null;
  feeField: string | null;
  /** 'CD' or 'LE' for disclosed-fee rules; absent on earlier saved versions. */
  feeSource?: 'CD' | 'LE' | null;
  currentFeeCents?: number | null;
  lastDisclosedLECents?: number | null;
  lastDisclosedCDCents?: number | null;
  ruleVersion: string;
  excessCents: number | null;
  evaluatedAt: string | null;
  checkedAt: string | null;
  reportId: string | null;
  orderId: string | null;
  associationStatus: 'matched' | 'waiting' | 'review' | null;
  associationReason: string | null;
  renameState: string;
  renameReason: string | null;
  pending: boolean;
  freshState?: 'none' | 'pending' | 'satisfied' | 'attention';
  /** Manual rerun request time, or null when the row has never been rerun by hand. */
  freshRequestedAt?: string | null;
  /** The manual rerun reuses completed text from the unchanged capture. */
  freshExtractionSkipped?: boolean;
  /** When the attachment text was last extracted by Docling. */
  extractedAt?: string | null;
  retrying?: boolean;
  needsAttention?: boolean;
  cures?: { requiredCents: number | null; appliedCents: number | null; checkedAt: string | null } | null;
  /** Loan-level alert stop recorded by a user; the finding stays visible but is not emailed or digested. */
  alertsStoppedAt?: string | null;
  alertsStoppedBy?: string | null;
  alertsStoppedReason?: string | null;
}

/** Column header for the disclosed-fee amount in the report, history ledger and digest. */
export const APPRAISAL_FEE_COLUMN_LABEL = 'Last Discl.';

export function usesDisclosedAppraisalFee(ruleVersion: string | undefined): boolean {
  return /^appraisal-invoice-v(?:[6-9]|\d{2,})$/.test(ruleVersion || '');
}

/** Row label for the amount an invoice was compared with. v6 always used the LE; v7 records its CD/LE source. */
export function appraisalFeeBasisLabel(ruleVersion: string | undefined, feeSource?: 'CD' | 'LE' | null): string {
  if (usesDisclosedAppraisalFee(ruleVersion)) {
    if (feeSource === 'CD') { return 'Last disclosed CD'; }
    if (feeSource === 'LE' || ruleVersion === 'appraisal-invoice-v6') { return 'Last disclosed LE'; }
    return 'Last disclosed';
  }
  if (/^appraisal-invoice-v[2-5]$/.test(ruleVersion || '')) { return 'Current fee at check'; }
  return ruleVersion === 'appraisal-invoice-v1' ? 'Recorded total fee' : 'Recorded fee';
}
